"use client";

import { getImageUrl } from "@/lib/api";
import { useAuth } from '@/context/AuthContext';
import { CurrencyProvider } from '@/context/CurrencyContext';
import CurrencyRateButton from '@/components/CurrencyRateButton';
import { useRouter, usePathname } from 'next/navigation';
import { useEffect, useState, useMemo, useRef, useSyncExternalStore } from 'react';
import Link from 'next/link';
import {
    LayoutDashboard,
    Home,
    Users,
    Settings,
    Settings2,
    LogOut,
    MessageSquare,
    Calendar,
    Briefcase,
    FileText,
    User,
    UserCheck,
    UserPlus,
    Target,
    Activity,
    Mail,
    ChevronRight,
    ChevronLeft,
    ChevronDown,
    Globe,
    Lock,
    Menu,
    X,
    Building,
    HelpCircle,
    Search,
    Megaphone,
    FileCheck,
    GitBranch,
    CreditCard,
    Calculator,
    TrendingUp,
    DollarSign,
    ShieldCheck,
    HardDrive,
    LayoutGrid,
    Plus,
    Sparkles,
    Check,
    Zap
} from 'lucide-react';
import ChatAssistant from '@/components/Assistant/ChatAssistant';
import NotificationSystem from '@/components/NotificationSystem';
import MasterAdminDarkLayout from '@/components/MasterAdminDarkLayout';
import OdooAppLauncher from '@/components/OdooAppLauncher';
import { isMasterAdminUser, isMasterRouteAllowed, MASTER_HOME_ROUTE } from '@/constants/masterAdmin';

// ==========================================
// WORKPLACES (ESPACIOS DE TRABAJO CREATIO)
// ==========================================
const WORKPLACES_REGULAR = [
    { id: 'sales', name: 'Inmobiliaria & Ventas', icon: '🏢', description: 'Comercial, inventario, pipeline y citas' },
    { id: 'marketing', name: 'Marketing & Ads', icon: '📢', description: 'Meta Ads, Baly Analyst y audiencias' },
    { id: 'operations', name: 'Operaciones & Legal', icon: '📑', description: 'Expedientes, contratos y comisiones' },
    { id: 'settings', name: 'Configuración & Empresa', icon: '⚙️', description: 'Usuarios, agencia, sitio web y seguridad' },
    { id: 'all', name: 'Todos los Módulos', icon: '🌐', description: 'Directorio completo del sistema' }
];

// Asignación de módulos a espacios de trabajo
const ITEM_WORKPLACES = {
    'Dashboard': ['sales'],
    'Contactos': ['sales', 'marketing'],
    'Propiedades': ['sales'],
    'Pipeline Leads': ['sales', 'marketing'],
    'Automatizaciones': ['sales', 'marketing', 'operations', 'settings'],
    'Calendario': ['sales'],
    'Mensajería': ['sales'],
    'WhatsApp': ['sales'],
    'Marketing': ['marketing', 'sales'],
    'Sitio Web': ['marketing', 'settings'],
    'Documentos': ['operations'],
    'Expedientes': ['operations'],
    'Baly Builder': ['operations', 'master'],
    'Herramientas': ['operations'],
    'Reportes': ['operations'],
    'Mi Perfil': ['settings', 'sales', 'master'],
    'Usuarios': ['settings'],
    'Ajustes': ['settings'],
    'Empresa': ['settings'],
    'Configuración de Empresa': ['settings'],
    'Ayuda': ['settings', 'sales', 'master'],
    'Agencias': ['master'],
    'Planes': ['master'],
    'Configuración': ['master'],
    'Auditoría': ['master'],
    'Respaldos': ['master'],
    'Plantillas': ['master'],
    'Debugger': ['master'],
};

// ==========================================
// BADGES DE MÓDULO · MONOCROMO + ACENTO REGE
// ==========================================
// En reposo los iconos son monocromos para no competir entre sí.
// Solo el módulo activo (o el submenú expandido) recibe el degradado de marca.
const ITEM_BADGE_ACTIVE = 'bg-[#E040FB]/20 text-white ring-1 ring-inset ring-[#E040FB]/40';
const ITEM_BADGE_IDLE = 'bg-white/10 text-white/70 group-hover:bg-white/20 group-hover:text-white';

// ── Nombre del asistente: almacén externo (localStorage + evento global) ──
// Se usa con useSyncExternalStore para evitar setState dentro de un efecto.
let assistantNameCache = null;

function readAssistantName() {
    if (typeof window === 'undefined') return 'Baly';
    if (assistantNameCache) return assistantNameCache;
    try {
        const cached = window.localStorage.getItem('ai_assistant_config');
        if (cached) {
            const parsed = JSON.parse(cached);
            if (parsed?.agent_name) {
                assistantNameCache = parsed.agent_name;
                return assistantNameCache;
            }
        }
    } catch {
        // Caché inválida: se usa el nombre por defecto
    }
    return 'Baly';
}

function subscribeAssistantName(onChange) {
    const handleIdentity = (event) => {
        if (event?.detail?.agent_name) {
            assistantNameCache = event.detail.agent_name;
            try {
                window.localStorage.setItem('ai_assistant_config', JSON.stringify(event.detail));
            } catch {
                // Sin persistencia: el nombre sigue vivo en memoria
            }
        }
        onChange();
    };
    const handleStorage = (event) => {
        if (event.key === 'ai_assistant_config') {
            assistantNameCache = null;
            onChange();
        }
    };
    window.addEventListener('ai-agent-identity-updated', handleIdentity);
    window.addEventListener('storage', handleStorage);
    return () => {
        window.removeEventListener('ai-agent-identity-updated', handleIdentity);
        window.removeEventListener('storage', handleStorage);
    };
}

export default function DashboardLayout({ children }) {
    const { user, logout, loading } = useAuth();
    const router = useRouter();
    const pathname = usePathname();

    // Identificar si es el Super Administrador global del SaaS
    const isMasterAdmin = isMasterAdminUser(user);

    // Estados de navegación
    const [expandedMenu, setExpandedMenu] = useState(null);
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
    // El menú móvil se cierra solo al cambiar de ruta: guardamos la ruta en la que se abrió
    const [mobileMenuPath, setMobileMenuPath] = useState(null);
    const isMobileMenuOpen = mobileMenuPath === pathname;
    const [pathnameAtToggle, setPathnameAtToggle] = useState(null);
    // Estados de Creatio UI
    const [isWorkplaceDropdownOpen, setIsWorkplaceDropdownOpen] = useState(false);
    const [menuSearchQuery, setMenuSearchQuery] = useState('');
    const [isQuickAddOpen, setIsQuickAddOpen] = useState(false);
    const [appLauncherPath, setAppLauncherPath] = useState(null);
    const isAppLauncherOpen = appLauncherPath === pathname;
    const [globalSearchQuery, setGlobalSearchQuery] = useState('');

    const workplaceDropdownRef = useRef(null);
    const quickAddRef = useRef(null);
    const appLauncherRef = useRef(null);

    // Cerrar popovers al hacer click afuera
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (workplaceDropdownRef.current && !workplaceDropdownRef.current.contains(e.target)) {
                setIsWorkplaceDropdownOpen(false);
            }
            if (quickAddRef.current && !quickAddRef.current.contains(e.target)) {
                setIsQuickAddOpen(false);
            }
            if (appLauncherRef.current && !appLauncherRef.current.contains(e.target)) {
                setAppLauncherPath(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Escuchar evento global para abrir el Lanzador de Módulos (Estilo Odoo 20)
    useEffect(() => {
        const handleOpenLauncher = () => setAppLauncherPath(pathname);
        window.addEventListener('open-app-launcher', handleOpenLauncher);
        return () => window.removeEventListener('open-app-launcher', handleOpenLauncher);
    }, [pathname]);

    // Definición completa de ítems de menú (solo agencias: el Super Admin usa MasterAdminDarkLayout)
    const menuItems = useMemo(() => {
        // Menú base de la inmobiliaria
        const baseItems = [
            { name: 'Dashboard', icon: LayoutDashboard, href: '/dashboard' },
            {
                name: 'Contactos',
                icon: Users,
                href: '/dashboard/contacts',
                subItems: [
                    { name: 'Todos los Contactos', href: '/dashboard/contacts' },
                    { name: 'Personales', href: '/dashboard/contacts?private=true', icon: Lock },
                    { name: 'Compradores', href: '/dashboard/contacts?type=Comprador', icon: UserCheck },
                    { name: 'Propietarios', href: '/dashboard/contacts?type=Propietario', icon: UserPlus },
                    { name: 'Corredores', href: '/dashboard/contacts?type=Corredor', icon: Users },
                    { name: 'LEADS', href: '/dashboard/contacts?type=LEAD', icon: Target }
                ]
            },
            {
                name: 'Propiedades',
                icon: Home,
                href: '/dashboard/properties',
                subItems: [
                    { name: 'Inventario General', href: '/dashboard/properties', icon: Home },
                    { name: 'Proyectos y Desarrollos', href: '/dashboard/developments', icon: Building }
                ]
            },
            { name: 'Pipeline Leads', icon: Target, href: '/dashboard/leads' },
            { name: 'Automatizaciones', icon: Zap, href: '/dashboard/automations' },
            { name: 'Sitio Web', icon: Globe, href: '/dashboard/website' },
            { name: 'Marketing', icon: Megaphone, href: '/dashboard/marketing' },
            {
                name: 'Herramientas',
                icon: Briefcase,
                href: '/dashboard/tools',
                subItems: [
                    { name: 'Calculadora Crédito', href: '/dashboard/tools/calculator', icon: Calculator },
                    { name: 'Proyección Rentabilidad (ROI)', href: '/dashboard/tools/roi-calculator', icon: TrendingUp },
                    { name: 'Comisiones (DSL)', href: '/dashboard/tools/commissions', icon: DollarSign }
                ]
            },
            {
                name: 'Calendario',
                icon: Calendar,
                href: '/dashboard/calendar',
                subItems: [
                    { name: 'Agenda de Citas', href: '/dashboard/calendar', icon: Calendar },
                    { name: 'Turnos por Proyecto', href: '/dashboard/calendar/shifts', icon: Building }
                ]
            },
            { name: 'Documentos', icon: FileText, href: '/dashboard/documents' },
            { name: 'Expedientes', icon: FileCheck, href: '/dashboard/workflows' },
            {
                name: 'Reportes',
                icon: FileText,
                href: '/dashboard/reports',
                subItems: [
                    { name: 'Cierres y Comisiones', href: '/dashboard/reports/commissions' }
                ]
            },
            { name: 'Mi Perfil', icon: User, href: '/dashboard/profile' },
            {
                name: 'WhatsApp',
                icon: MessageSquare,
                href: '/dashboard/messaging/conversations',
                subItems: [
                    { name: 'Bandeja WhatsApp', href: '/dashboard/messaging/conversations?channel=whatsapp', icon: MessageSquare },
                    { name: 'Bandeja Messenger', href: '/dashboard/messaging/conversations?channel=messenger', icon: MessageSquare },
                    { name: 'Bandeja Correo', href: '/dashboard/messaging/conversations?channel=email', icon: Mail },
                    { name: 'Estado WhatsApp', href: '/dashboard/settings?tab=whatsapp', icon: Settings }
                ]
            },
            {
                name: 'Usuarios',
                icon: Users,
                href: '/dashboard/users',
                subItems: [
                    { name: 'Gestión de Usuarios', href: '/dashboard/users' },
                ]
            },
            {
                name: 'Ajustes',
                icon: Settings,
                href: '/dashboard/settings',
                subItems: [
                    { name: 'General', href: '/dashboard/settings' },
                    { name: 'Empresa', href: '/dashboard/settings/agency', icon: Building },
                    { name: 'Seguridad', href: '/dashboard/settings/security' }
                ]
            },
            { name: 'Ayuda', icon: HelpCircle, href: '/dashboard/help' },
        ];

        return baseItems.filter(item => {
            if (item.name === 'Usuarios' || item.name === 'Ajustes') {
                return user?.role === 'admin';
            }
            return true;
        });
    }, [user]);

    // El Super Administrador solo puede operar la consola Master: cualquier ruta del CRM
    // de agencias (incluido el lanzador /dashboard) se redirige a su Dashboard SaaS.
    const isMasterOnForbiddenRoute = isMasterAdmin && !isMasterRouteAllowed(pathname);

    // Redirección y autenticación
    useEffect(() => {
        if (loading) return;
        if (!user) {
            router.push('/login');
        } else if (isMasterOnForbiddenRoute) {
            router.replace(MASTER_HOME_ROUTE);
        }
    }, [user, loading, router, isMasterOnForbiddenRoute]);

    // Auto-detectar Workplace según la ruta actual (sin setState en useEffect)
    const [workplaceOverride, setWorkplaceOverride] = useState({ pathname: null, id: 'sales' });

    const autoWorkplace = useMemo(() => {
        if (pathname.startsWith('/dashboard/marketing')) {
            return 'marketing';
        }
        if (
            pathname.startsWith('/dashboard/tools') ||
            pathname.startsWith('/dashboard/workflows') ||
            pathname.startsWith('/dashboard/documents') ||
            pathname.startsWith('/dashboard/reports')
        ) {
            return 'operations';
        }
        if (
            pathname.startsWith('/dashboard/settings') ||
            pathname.startsWith('/dashboard/users') ||
            pathname.startsWith('/dashboard/help') ||
            pathname.startsWith('/dashboard/profile')
        ) {
            return 'settings';
        }
        return 'sales';
    }, [pathname]);

    const selectedWorkplace = workplaceOverride.pathname === pathname ? workplaceOverride.id : autoWorkplace;
    const setSelectedWorkplace = (id) => setWorkplaceOverride({ pathname, id });

    // Auto-expandir menú padre de la ruta activa
    const autoExpandedMenu = useMemo(() => {
        const activeItem = menuItems.find(item =>
            item.subItems?.some(sub => pathname === sub.href)
        );
        return activeItem ? activeItem.name : null;
    }, [pathname, menuItems]);

    const pathnameChangedSinceToggle = pathnameAtToggle !== pathname;
    const effectiveExpandedMenu = pathnameChangedSinceToggle ? autoExpandedMenu : expandedMenu;

    const toggleMenu = (name) => {
        setPathnameAtToggle(pathname);
        if (expandedMenu === name) {
            setExpandedMenu(null);
        } else {
            setExpandedMenu(name);
        }
    };

    // Validación de planes y permisos según el plan de suscripción de la agencia
    const isLocked = (name) => {
        if (loading || !user?.planLimits) return false;
        const limits = user?.planLimits || {};
        const features = limits.can_use_features || {};
        const services = limits.services || {};

        if (name === 'Proyectos y Desarrollos' && !features.developments_module && !services.developments_module) return true;
        if (name === 'Documentos' && !features.docs_semantic && !services.semantic_docs) return true;
        if (name === 'Calculadora Crédito' && !features.calculadora_credito && !services.mortgage_calculator) return true;
        if (name === 'Sitio Web' && !features.netlify_web && !services.website_sync) return true;
        if (name === 'Bandeja Messenger' && !features.messenger_sync && !services.messenger_sync) return true;
        if (name === 'Bandeja WhatsApp' && !features.whatsapp_sync && !services.whatsapp_sync) return true;
        if (name === 'Marketing' && !services.marketing_module && !features.marketing_module) return true;
        if (name === 'Comisiones (DSL)' && !services.commissions) return true;
        if (name === 'Expedientes' && !services.expedientes) return true;
        
        return false;
    };

    // Lista de Workplaces disponibles
    const currentWorkplaces = WORKPLACES_REGULAR;

    const activeWorkplaceObj = currentWorkplaces.find(w => w.id === selectedWorkplace) || currentWorkplaces[0];

    // Filtrar ítems visibles por Workplace y por Buscador de Menú
    const displayedMenuItems = useMemo(() => {
        let items = menuItems;

        // Si el usuario está buscando texto en el menú lateral, buscar en TODO el catálogo
        if (menuSearchQuery.trim()) {
            const query = menuSearchQuery.toLowerCase().trim();
            return items.filter(item => {
                const matchName = item.name.toLowerCase().includes(query);
                const matchSub = item.subItems?.some(s => s.name.toLowerCase().includes(query));
                return matchName || matchSub;
            });
        }

        // Si no busca, filtrar por el Workplace seleccionado
        if (selectedWorkplace === 'all') {
            return items;
        }

        return items.filter(item => {
            const assignedWorkplaces = ITEM_WORKPLACES[item.name] || ['sales'];
            return assignedWorkplaces.includes(selectedWorkplace);
        });
    }, [menuItems, selectedWorkplace, menuSearchQuery]);

    // Nombre dinámico del asistente (sincronizado con localStorage y el evento global)
    const assistantName = useSyncExternalStore(
        subscribeAssistantName,
        readAssistantName,
        () => 'Baly'
    );

    // Función para invocar al Asistente AI Copilot
    const handleToggleBalyAssistant = () => {
        window.dispatchEvent(new CustomEvent('toggle-baly-assistant'));
    };

    // Manejar búsqueda global
    const handleGlobalSearch = (e) => {
        e.preventDefault();
        if (!globalSearchQuery.trim()) return;
        router.push(`/dashboard/contacts?search=${encodeURIComponent(globalSearchQuery.trim())}`);
    };

    if (loading) return null;
    if (!user) return null;

    // El Super Administrador global del SaaS SIEMPRE usa su consola DARK exclusiva.
    // Mientras se redirige desde una ruta del CRM de agencias no se renderiza nada.
    if (isMasterAdmin) {
        if (isMasterOnForbiddenRoute) return null;
        return (
            <MasterAdminDarkLayout user={user} logout={logout}>
                {children}
            </MasterAdminDarkLayout>
        );
    }

    // Si la ruta es exactamente /dashboard (Menú Principal de Aplicaciones Estilo Odoo 20)
    // renderizar a pantalla completa limpia sin barras invasivas (tal cual la captura del usuario)
    if (pathname === '/dashboard') {
        return (
            <div className="min-h-screen w-full font-sans bg-gradient-to-br from-[#ebe6f2] via-[#f5f3f8] to-[#e4e0ee] relative overflow-x-hidden flex flex-col">
                {children}
                <ChatAssistant />
            </div>
        );
    }

    return (
        <CurrencyProvider>
        <div className="h-screen w-full flex flex-col font-sans overflow-hidden bg-[#f4f7fa]">
            
            {/* ======================================================== */}
            {/* TOPBAR GLOBAL CREATIO ENTERPRISE (DEEP NAVY GRADIENT)   */}
            {/* ======================================================== */}
            <header className="h-[56px] bg-gradient-to-r from-[#2F1A29] via-[#3F2A3C] to-[#543550] text-white border-b border-white/10 px-3 sm:px-5 flex items-center justify-between shrink-0 z-50 shadow-xs select-none">
                
                {/* LADO IZQUIERDO: App Launcher, Brand, Quick Add y Global Search */}
                <div className="flex items-center gap-2 sm:gap-4 flex-1 max-w-2xl">
                    
                    {/* Botón Móvil Menú Drawer */}
                    <button
                        onClick={() => setMobileMenuPath(isMobileMenuOpen ? null : pathname)}
                        className="p-1.5 text-white/80 hover:text-white hover:bg-white/10 rounded-lg md:hidden transition-colors cursor-pointer"
                        title="Abrir menú"
                    >
                        {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
                    </button>

                    {/* App Launcher (Waffle 9-dots estilo Odoo) */}
                    <div className="relative">
                        <button
                            onClick={() => setAppLauncherPath(pathname)}
                            className={`p-2 rounded-lg transition-colors cursor-pointer flex items-center justify-center ${
                                isAppLauncherOpen ? 'bg-white/25 text-white' : 'text-white/80 hover:text-white hover:bg-white/10'
                            }`}
                            title="Lanzador de Módulos (Estilo Odoo)"
                        >
                            <LayoutGrid size={19} />
                        </button>
                    </div>

                    {/* Modal de Aplicaciones Estilo Odoo */}
                    <OdooAppLauncher
                        isOpen={isAppLauncherOpen}
                        onClose={() => setAppLauncherPath(null)}
                        isMasterAdmin={false}
                        setSelectedWorkplace={setSelectedWorkplace}
                    />


                    {/* Logo y marca de la agencia */}
                    <div className="flex items-center gap-2">
                        <Link href="/dashboard" className="flex items-center gap-2 cursor-pointer group">
                            {user?.agency?.logo_url ? (
                                <img 
                                    src={getImageUrl(user.agency.logo_url)} 
                                    alt={user.agency.name || 'REGE'} 
                                    className="h-7 w-7 rounded-md object-cover bg-white p-0.5 shadow-2xs" 
                                />
                            ) : (
                                <div className="h-7 w-7 rounded-md bg-white/20 flex items-center justify-center font-black text-white text-xs">
                                    R
                                </div>
                            )}
                            <span className="font-extrabold text-sm sm:text-base tracking-tight text-white hidden sm:inline">
                                {user?.agency?.name ? user.agency.name.split(' ')[0] : 'REGE'}
                                <span className="font-normal text-white/70 ml-1 text-xs">CRM</span>
                            </span>
                        </Link>

                        {/* Botón Quick-Add Creatio (+) */}
                        <div className="relative ml-1" ref={quickAddRef}>
                            <button
                                onClick={() => setIsQuickAddOpen(!isQuickAddOpen)}
                                className="w-6 h-6 rounded-full bg-white/20 hover:bg-white/30 text-white flex items-center justify-center font-bold transition-all cursor-pointer shadow-2xs hover:scale-105"
                                title="Creación rápida (+)"
                            >
                                <Plus size={14} />
                            </button>

                            {/* Dropdown Quick Add */}
                            {isQuickAddOpen && (
                                <div className="absolute left-0 mt-2 w-56 bg-white rounded-lg shadow-2xl border border-slate-200 text-slate-800 py-1.5 z-50 animate-in fade-in zoom-in-95 duration-150">
                                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 px-3 py-1">
                                        Crear Registro Rápido
                                    </div>
                                    {(
                                        <>
                                            <Link
                                                href="/dashboard/contacts?action=new"
                                                onClick={() => setIsQuickAddOpen(false)}
                                                className="flex items-center gap-2 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 hover:text-[#6A1B9A] transition-colors"
                                            >
                                                <div className="w-5 h-5 rounded bg-purple-100 text-purple-700 flex items-center justify-center">
                                                    <Users size={12} />
                                                </div>
                                                <span>Nuevo Contacto</span>
                                            </Link>
                                            <Link
                                                href="/dashboard/properties"
                                                onClick={() => setIsQuickAddOpen(false)}
                                                className="flex items-center gap-2 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 hover:text-[#6A1B9A] transition-colors"
                                            >
                                                <div className="w-5 h-5 rounded bg-emerald-100 text-emerald-700 flex items-center justify-center">
                                                    <Home size={12} />
                                                </div>
                                                <span>Nueva Propiedad</span>
                                            </Link>
                                            <Link
                                                href="/dashboard/leads"
                                                onClick={() => setIsQuickAddOpen(false)}
                                                className="flex items-center gap-2 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 hover:text-[#6A1B9A] transition-colors"
                                            >
                                                <div className="w-5 h-5 rounded bg-rose-100 text-rose-700 flex items-center justify-center">
                                                    <Target size={12} />
                                                </div>
                                                <span>Nuevo Lead al Pipeline</span>
                                            </Link>
                                            <Link
                                                href="/dashboard/calendar"
                                                onClick={() => setIsQuickAddOpen(false)}
                                                className="flex items-center gap-2 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 hover:text-[#6A1B9A] transition-colors"
                                            >
                                                <div className="w-5 h-5 rounded bg-[#F3E5FA] text-[#6A1B9A] flex items-center justify-center">
                                                    <Calendar size={12} />
                                                </div>
                                                <span>Agendar Cita</span>
                                            </Link>
                                            <div className="h-px bg-slate-100 my-1" />
                                            <Link
                                                href="/dashboard/marketing"
                                                onClick={() => setIsQuickAddOpen(false)}
                                                className="flex items-center gap-2 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 hover:text-[#6A1B9A] transition-colors"
                                            >
                                                <div className="w-5 h-5 rounded bg-sky-100 text-sky-700 flex items-center justify-center">
                                                    <Megaphone size={12} />
                                                </div>
                                                <span>Diseñar Anuncio Meta</span>
                                            </Link>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Barra de Búsqueda Global Omnicanal */}
                    <form onSubmit={handleGlobalSearch} className="relative hidden md:flex items-center w-full max-w-xs lg:max-w-sm">
                        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-white/60 pointer-events-none" />
                        <input
                            type="text"
                            value={globalSearchQuery}
                            onChange={(e) => setGlobalSearchQuery(e.target.value)}
                            placeholder="Buscar contactos, inmuebles, leads..."
                            className="w-full bg-white/15 hover:bg-white/20 focus:bg-white/25 text-white placeholder-white/60 text-xs rounded-lg pl-8 pr-3 py-1.5 border border-white/20 focus:outline-none focus:ring-1 focus:ring-white/40 transition-colors"
                        />
                    </form>
                </div>

                {/* LADO DERECHO: Baly AI Copilot, Notificaciones, Ajustes y Usuario */}
                <div className="flex items-center gap-2 sm:gap-3">
                    
                    {/* Botón Creatio.ai / Copilot */}
                    <button
                        onClick={handleToggleBalyAssistant}
                        className="flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-lg bg-white/15 hover:bg-white/25 border border-white/25 text-white font-bold text-xs transition-colors cursor-pointer shadow-2xs"
                        title={`Abrir ${assistantName} AI Copilot`}
                    >
                        <Sparkles size={14} className="text-amber-300 animate-pulse" />
                        <span className="font-extrabold tracking-tight">ai</span>
                        <span className="hidden sm:inline font-medium text-[11px] text-white/90">{assistantName}</span>
                    </button>

                    {/* Notificaciones */}
                    <div className="text-white [&_button]:text-white/80 [&_button:hover]:text-white [&_button:hover]:bg-white/10">
                        <NotificationSystem />
                    </div>

                    {/* Tipo de cambio global (solo administradores) */}
                    {(user?.role === 'admin' || user?.role === 'superadmin') && <CurrencyRateButton />}

                    {/* Ayuda */}
                    <Link
                        href="/dashboard/help"
                        className="p-1.5 rounded-lg text-white/80 hover:text-white hover:bg-white/10 transition-colors hidden sm:flex cursor-pointer"
                        title="Centro de Ayuda"
                    >
                        <HelpCircle size={18} />
                    </Link>

                    {/* Ajustes Rápidos */}
                    {user?.role === 'admin' && (
                        <Link
                            href="/dashboard/settings"
                            className="p-1.5 rounded-lg text-white/80 hover:text-white hover:bg-white/10 transition-colors hidden sm:flex cursor-pointer"
                            title="Ajustes del Sistema"
                        >
                            <Settings size={18} />
                        </Link>
                    )}

                    <div className="h-5 w-px bg-white/20 mx-1 hidden sm:block" />

                    {/* Perfil de Usuario & Logout */}
                    <div className="flex items-center gap-2">
                        <Link href="/dashboard/profile" className="flex items-center gap-2 cursor-pointer group">
                            <div className="w-8 h-8 rounded-full overflow-hidden border border-white/30 bg-white shadow-2xs">
                                <img
                                    src={user?.profile_picture_url ? getImageUrl(user.profile_picture_url) : 'https://www.gravatar.com/avatar/000?d=mp'}
                                    className="w-full h-full object-cover"
                                    alt="Usuario"
                                    onError={(e) => { e.target.src = "https://www.gravatar.com/avatar/000?d=mp"; }}
                                />
                            </div>
                            <div className="hidden lg:flex flex-col text-left">
                                <span className="text-xs font-bold text-white truncate max-w-[120px] leading-tight">
                                    {user?.full_name || user?.username}
                                </span>
                                <span className="text-[10px] text-white/70 uppercase font-semibold tracking-wider">
                                    {user?.role || 'Asesor'}
                                </span>
                            </div>
                        </Link>

                        <button
                            onClick={logout}
                            className="p-1.5 text-white/70 hover:text-red-200 hover:bg-red-500/20 rounded-lg transition-colors cursor-pointer"
                            title="Cerrar Sesión"
                        >
                            <LogOut size={16} />
                        </button>
                    </div>

                </div>

            </header>

            {/* ======================================================== */}
            {/* CUERPO PRINCIPAL: SIDEBAR CREATIO + LIENZO DE CONTENIDO  */}
            {/* ======================================================== */}
            <div className="flex-1 flex overflow-hidden relative w-full h-[calc(100vh-56px)]">

                {/* Overlay para Móvil */}
                {isMobileMenuOpen && (
                    <div
                        className="fixed inset-0 bg-slate-900/60 z-40 md:hidden backdrop-blur-xs animate-in fade-in duration-200"
                        onClick={() => setMobileMenuPath(null)}
                    />
                )}

                {/* ======================================================== */}
                {/* SIDEBAR · CIRUELA DE MARCA (GRADACIÓN DEL LOGIN)        */}
                {/* ======================================================== */}
                <aside className={`
                    bg-gradient-to-b from-[#2F1A29] via-[#3D283A] to-[#4A3045] text-white flex flex-col h-full z-40 border-r border-white/10 shadow-lg transition-all duration-200 ease-in-out shrink-0
                    ${isSidebarCollapsed ? 'w-[72px]' : 'w-[250px]'}
                    ${isMobileMenuOpen ? 'fixed inset-y-0 left-0 top-[56px] z-50 w-[250px]' : 'max-md:hidden'}
                `}>

                    {/* Ancla de plataforma: símbolo REGE (lockup al expandir) centrado */}
                    <div className="flex shrink-0 items-center justify-center border-b border-white/10 px-3 py-2.5">
                        <Link href="/dashboard" className="flex items-center justify-center cursor-pointer opacity-90 transition-opacity hover:opacity-100" title="REGE — Inicio">
                            <img
                                src={isSidebarCollapsed ? '/brand/rege-symbol-white.svg' : '/brand/rege-lockup-horizontal-white.svg'}
                                alt="REGE"
                                draggable={false}
                                className={isSidebarCollapsed ? 'h-6 w-6' : 'h-5 w-auto'}
                            />
                        </Link>
                    </div>

                    {/* CABECERA DEL MENÚ: Selector de Workplace y Buscador Interno */}
                    {!isSidebarCollapsed && (
                        <div className="p-3 border-b border-white/10 space-y-2 shrink-0">
                            
                            {/* Selector de Workplace */}
                            {(
                                <div className="relative" ref={workplaceDropdownRef}>
                                    <button
                                        onClick={() => setIsWorkplaceDropdownOpen(!isWorkplaceDropdownOpen)}
                                        className="w-full bg-white/10 hover:bg-white/15 border border-white/20 text-white rounded-lg px-2.5 py-2 text-[13px] font-semibold flex items-center justify-between transition-colors cursor-pointer shadow-2xs"
                                    >
                                        <div className="flex items-center gap-2 truncate">
                                            <span className="text-sm">{activeWorkplaceObj.icon}</span>
                                            <span className="truncate">{activeWorkplaceObj.name}</span>
                                        </div>
                                        <ChevronDown size={14} className={`text-white/70 transition-transform ${isWorkplaceDropdownOpen ? 'rotate-180' : ''}`} />
                                    </button>

                                    {/* Dropdown de Workplaces */}
                                    {isWorkplaceDropdownOpen && (
                                        <div className="absolute left-0 right-0 mt-1.5 bg-white rounded-lg shadow-xl border border-slate-200 text-slate-800 py-1 z-50 animate-in fade-in duration-100">
                                            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 px-3 py-1">
                                                Espacios de Trabajo
                                            </div>
                                            {currentWorkplaces.map((wp) => (
                                                <button
                                                    key={wp.id}
                                                    onClick={() => {
                                                        setSelectedWorkplace(wp.id);
                                                        setIsWorkplaceDropdownOpen(false);
                                                        setMenuSearchQuery('');
                                                    }}
                                                    className={`w-full text-left px-3 py-2 text-xs font-semibold flex items-center justify-between transition-colors cursor-pointer ${
                                                        selectedWorkplace === wp.id 
                                                            ? 'bg-[#F3E5FA] text-[#6A1B9A] font-bold' 
                                                            : 'text-slate-700 hover:bg-slate-50'
                                                    }`}
                                                >
                                                    <div className="flex items-center gap-2">
                                                        <span>{wp.icon}</span>
                                                        <span>{wp.name}</span>
                                                    </div>
                                                    {selectedWorkplace === wp.id && (
                                                        <Check size={14} className="text-[#6A1B9A]" />
                                                    )}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Filtro Rápido en Vivo de Secciones (Buscador del menú) */}
                            <div className="relative">
                                <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-white/50 pointer-events-none" />
                                <input
                                    type="text"
                                    value={menuSearchQuery}
                                    onChange={(e) => setMenuSearchQuery(e.target.value)}
                                    placeholder="Buscar sección o página..."
                                    className="w-full bg-white/10 hover:bg-white/15 focus:bg-white/20 text-white placeholder-white/50 text-xs rounded-lg pl-8 pr-7 py-1.5 border border-white/15 focus:outline-none focus:ring-1 focus:ring-white/30 transition-colors"
                                />
                                {menuSearchQuery && (
                                    <button
                                        onClick={() => setMenuSearchQuery('')}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 text-white/60 hover:text-white p-0.5 cursor-pointer"
                                    >
                                        <X size={12} />
                                    </button>
                                )}
                            </div>

                        </div>
                    )}

                    {/* LISTA VERTICAL DE NAVEGACIÓN (SCROLLBAR OCULTO Y ELEGANTE) */}
                    <nav className="flex-1 overflow-y-auto py-2.5 px-2 space-y-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
                        
                        {displayedMenuItems.length === 0 && (
                            <div className="p-4 text-center text-xs text-white/70">
                                No se encontraron secciones para &quot;{menuSearchQuery}&quot;
                            </div>
                        )}

                        {displayedMenuItems.map((item) => {
                            const hasSub = item.subItems && item.subItems.length > 0;
                            const isExpanded = effectiveExpandedMenu === item.name;
                            const isActive = pathname === item.href || (item.href !== '/dashboard' && pathname.startsWith(item.href) && !hasSub);
                            const locked = isLocked(item.name);
                            const badgeClass = isActive || isExpanded ? ITEM_BADGE_ACTIVE : ITEM_BADGE_IDLE;

                            return (
                                <div key={item.name} className="flex flex-col">
                                    {hasSub ? (
                                        <button
                                            onClick={() => toggleMenu(item.name)}
                                            className={`group w-full flex items-center justify-between border-l-[3px] px-2.5 py-2 rounded-lg text-[13px] font-medium transition-all cursor-pointer ${
                                                isExpanded
                                                    ? 'bg-white/10 text-white shadow-2xs border-[#E040FB] font-semibold'
                                                    : 'border-transparent text-white/90 hover:bg-white/10 hover:text-white'
                                            } ${isSidebarCollapsed ? 'justify-center px-0' : ''}`}
                                            title={isSidebarCollapsed ? item.name : ''}
                                        >
                                            <div className="flex items-center gap-2.5 truncate">
                                                <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 shadow-2xs ${badgeClass}`}>
                                                    <item.icon size={15} />
                                                </div>
                                                {!isSidebarCollapsed && (
                                                    <span className="truncate tracking-[0.01em]">{item.name}</span>
                                                )}
                                            </div>
                                            {!isSidebarCollapsed && (
                                                <ChevronDown 
                                                    size={14} 
                                                    className={`text-white/60 transition-transform ${isExpanded ? 'rotate-180 text-white' : ''}`} 
                                                />
                                            )}
                                        </button>
                                    ) : locked ? (
                                        <button
                                            onClick={() => alert(`El módulo "${item.name}" requiere un plan superior. ¡Mejora tu suscripción para desbloquearlo!`)}
                                            className={`w-full flex items-center justify-between border-l-[3px] border-transparent px-2.5 py-2 rounded-lg text-[13px] font-medium transition-all text-white/40 cursor-not-allowed opacity-60 ${isSidebarCollapsed ? 'justify-center px-0' : ''}`}
                                            title={isSidebarCollapsed ? `${item.name} (Bloqueado)` : ''}
                                        >
                                            <div className="flex items-center gap-2.5 truncate">
                                                <div className="w-7 h-7 rounded-lg bg-white/10 text-white/40 flex items-center justify-center shrink-0">
                                                    <item.icon size={15} />
                                                </div>
                                                {!isSidebarCollapsed && (
                                                    <span className="truncate tracking-[0.01em]">{item.name}</span>
                                                )}
                                            </div>
                                            {!isSidebarCollapsed && <Lock size={12} className="text-amber-300" />}
                                        </button>
                                    ) : (
                                        <Link
                                            href={item.href}
                                            onClick={() => setMobileMenuPath(null)}
                                            className={`group flex items-center justify-between border-l-[3px] px-2.5 py-2 rounded-lg text-[13px] font-medium transition-all cursor-pointer ${
                                                isActive
                                                    ? 'bg-white/10 text-white shadow-2xs border-[#E040FB] font-semibold'
                                                    : 'border-transparent text-white/90 hover:bg-white/10 hover:text-white'
                                            } ${isSidebarCollapsed ? 'justify-center px-0' : ''}`}
                                            title={isSidebarCollapsed ? item.name : ''}
                                        >
                                            <div className="flex items-center gap-2.5 truncate">
                                                <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 shadow-2xs ${badgeClass}`}>
                                                    <item.icon size={15} />
                                                </div>
                                                {!isSidebarCollapsed && (
                                                    <span className="truncate tracking-[0.01em]">{item.name}</span>
                                                )}
                                            </div>
                                        </Link>
                                    )}

                                    {/* Submenu Desplegable */}
                                    {hasSub && isExpanded && !isSidebarCollapsed && (
                                        <div className="ml-5 pl-4 border-l border-white/20 my-1 space-y-0.5 animate-in slide-in-from-top-1 duration-150">
                                            {item.subItems.map((sub) => {
                                                const isSubActive = pathname === sub.href;
                                                const subLocked = isLocked(sub.name);

                                                if (subLocked) {
                                                    return (
                                                        <button
                                                            key={sub.name}
                                                            onClick={() => alert(`El módulo "${sub.name}" requiere un plan superior.`)}
                                                            className="flex items-center justify-between w-full px-2 py-1.5 text-[12px] text-white/40 cursor-not-allowed opacity-60"
                                                        >
                                                            <span className="truncate">{sub.name}</span>
                                                            <Lock size={11} className="text-amber-300" />
                                                        </button>
                                                    );
                                                }

                                                return (
                                                    <Link
                                                        key={sub.name}
                                                        href={sub.href}
                                                        onClick={() => setMobileMenuPath(null)}
                                                        className={`flex items-center gap-2 px-2 py-1.5 rounded-md text-[12px] font-medium transition-colors cursor-pointer ${
                                                            isSubActive
                                                                ? 'bg-white/10 text-white font-bold'
                                                                : 'text-white/75 hover:text-white hover:bg-white/10'
                                                        }`}
                                                    >
                                                        {sub.icon && <sub.icon size={12} className="opacity-70" />}
                                                        <span className="truncate">{sub.name}</span>
                                                    </Link>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        })}

                    </nav>

                    {/* BOTÓN COLAPSAR / EXPANDIR (DESKTOP) */}
                    <div className="p-2 border-t border-white/10 shrink-0 hidden md:block">
                        <button
                            onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                            className="w-full flex items-center justify-center p-2 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors cursor-pointer text-xs font-medium gap-2"
                            title={isSidebarCollapsed ? 'Expandir barra lateral' : 'Colapsar barra lateral'}
                        >
                            {isSidebarCollapsed ? (
                                <ChevronRight size={16} />
                            ) : (
                                <>
                                    <ChevronLeft size={16} />
                                    <span>Colapsar Menú</span>
                                </>
                            )}
                        </button>
                    </div>

                </aside>

                {/* ======================================================== */}
                {/* ÁREA DE CONTENIDO (LIENZO CREATIO ENTERPRISE LIMPIO)     */}
                {/* ======================================================== */}
                <main className="flex-1 h-full overflow-y-auto overflow-x-hidden relative bg-[#f4f7fa]">
                    <div className={`w-full min-w-0 mx-auto flex flex-col ${
                        pathname.startsWith('/dashboard/messaging/conversations')
                            ? 'p-0 max-w-none' 
                            : 'w-full max-w-[1850px] p-2 sm:p-3.5 md:px-4 md:py-3'
                    }`}>
                        {children}
                    </div>
                </main>


            </div>

            {/* Asistente Baly IA Integrado */}
            <ChatAssistant />

        </div>
        </CurrencyProvider>
    );
}


